const SIZE_SPLIT_SELECTOR = '[data-codex-size-split]';
const SOURCE_CLASS = 'codex-size-split-source';
const DIMENSION_PATTERN = /(\d+(?:[,.]\d+)?)\s*(?:x|×)\s*(\d+(?:[,.]\d+)?)/i;

const normalizeDimension = (value) => value.replace(',', '.');

const formatDimension = (value) => {
  const number = Number(normalizeDimension(value));
  if (!Number.isFinite(number)) return `${value} cm`;
  return `${Number.isInteger(number) ? number : number.toString().replace('.', ',')} cm`;
};

const numericDimension = (value) => {
  const number = Number(normalizeDimension(value));
  return Number.isFinite(number) ? number : 0;
};

const byDimension = (a, b) => numericDimension(a) - numericDimension(b);

let infoId = 0;

const createInfoIcon = () => {
  const icon = document.createElement('theme-icon');
  icon.className = 'icon icon--info codex-size-split__info-icon';
  icon.dataset.iconStyle = 'sharp';
  icon.dataset.iconName = 'info';
  icon.dataset.iconVariant = 'default';
  icon.setAttribute('aria-hidden', 'true');
  icon.style.setProperty('--icon-url', "url('https://fonts.gstatic.com/s/i/short-term/release/materialsymbolssharp/info/default/20px.svg')");
  return icon;
};

const getLabelForInput = (input) => {
  if (!input.id) return input.value;
  const label = input.closest('variant-picker')?.querySelector(`label[for="${CSS.escape(input.id)}"]`);
  return label?.textContent?.trim() || input.value;
};

const parseDimension = (value) => {
  const match = value.match(DIMENSION_PATTERN);
  if (!match) return null;
  return {
    width: normalizeDimension(match[1]),
    height: normalizeDimension(match[2]),
  };
};

const getControlValue = (control) => {
  if (control instanceof HTMLOptionElement) return control.value || control.textContent.trim();
  return control.value || getLabelForInput(control);
};

const isAvailable = (control) => {
  if (control instanceof HTMLOptionElement) return control.getAttribute('aria-disabled') !== 'true' && !control.disabled;
  return control.getAttribute('aria-disabled') !== 'true' && !control.disabled;
};

const getOptionName = (container, controls) => {
  const namedControl = controls.find((control) => control.name);
  if (namedControl?.name) return namedControl.name.replace(/^option-/, '').trim();
  const label = container.querySelector('.variant-picker__option-name')?.textContent || '';
  return label.split(':')[0].trim();
};

const findOptionGroups = (picker) => {
  const groups = [];

  picker.querySelectorAll('.variant-picker__option').forEach((container) => {
    const select = container.querySelector('select[name^="option-"]');
    if (select) {
      groups.push({
        container,
        type: 'select',
        select,
        controls: Array.from(select.options),
      });
      return;
    }

    const controls = Array.from(container.querySelectorAll('input[type="radio"][name^="option-"]'));
    if (controls.length > 0) {
      groups.push({
        container,
        type: 'radio',
        controls,
      });
    }
  });

  return groups;
};

const buildSizeGroup = (group) => {
  const combinations = group.controls
    .map((control) => {
      const label = getControlValue(control);
      const dimension = parseDimension(label);
      if (!dimension) return null;

      return {
        ...dimension,
        key: `${dimension.width}|${dimension.height}`,
        label,
        control,
        available: isAvailable(control),
        selected:
          control instanceof HTMLOptionElement
            ? control.selected
            : control instanceof HTMLInputElement && control.checked,
      };
    })
    .filter(Boolean);

  const optionName = getOptionName(group.container, group.controls).toLowerCase();
  const nameLooksRelevant = /taille|size|dimension|largeur|hauteur/.test(optionName);
  const hasEnoughCombinations = combinations.length >= 2 && combinations.length >= Math.ceil(group.controls.length * 0.6);
  const widths = [...new Set(combinations.map((item) => item.width))].sort(byDimension);
  const heights = [...new Set(combinations.map((item) => item.height))].sort(byDimension);

  if (!hasEnoughCombinations || widths.length < 1 || heights.length < 1 || !nameLooksRelevant) return null;

  return {
    ...group,
    combinations,
    widths,
    heights,
  };
};

const findSizeGroup = (picker) => {
  for (const group of findOptionGroups(picker)) {
    const sizeGroup = buildSizeGroup(group);
    if (sizeGroup) return sizeGroup;
  }

  return null;
};

const selectOriginalControl = (group, combination) => {
  if (!combination || !combination.available) return;

  if (group.type === 'select' && group.select) {
    group.select.value = combination.control.value;
    combination.control.selected = true;
    group.select.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  if (combination.control instanceof HTMLInputElement) {
    combination.control.checked = true;
    combination.control.dispatchEvent(new Event('change', { bubbles: true }));
  }
};

const findSelectedCombination = (group) =>
  group.combinations.find((item) => item.selected) || group.combinations.find((item) => item.available) || group.combinations[0];

const findCombination = (group, width, height) =>
  group.combinations.find((item) => item.width === width && item.height === height);

const findBestHeight = (group, width, preferredHeight) => {
  if (preferredHeight && findCombination(group, width, preferredHeight)?.available) return preferredHeight;
  return group.combinations.find((item) => item.width === width && item.available)?.height || preferredHeight;
};

const updateButtons = (root, group, state) => {
  root.querySelectorAll('[data-codex-width]').forEach((button) => {
    const active = button.dataset.codexWidth === state.width;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
  });

  root.querySelectorAll('[data-codex-height]').forEach((button) => {
    const combination = findCombination(group, state.width, button.dataset.codexHeight);
    const active = button.dataset.codexHeight === state.height;
    const available = Boolean(combination?.available);
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.disabled = !available;
    button.setAttribute('aria-disabled', available ? 'false' : 'true');
  });
};

const createButton = ({ label, value, type, active, disabled }) => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `codex-size-split__option${active ? ' is-active' : ''}`;
  button.dataset[type === 'width' ? 'codexWidth' : 'codexHeight'] = value;
  button.setAttribute('aria-pressed', active ? 'true' : 'false');
  if (disabled) {
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.title = 'Indisponible avec cette largeur';
  }
  button.textContent = label;
  return button;
};

const createPopupProxy = (group) => {
  const sourceOpener = group.container.querySelector('dialog-component [data-ref="popup-toggle"]');
  const label = sourceOpener?.textContent?.trim() || sourceOpener?.getAttribute('aria-label')?.trim();

  if (!sourceOpener || !label) return null;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'codex-size-split__popup-link';
  button.textContent = label;
  button.addEventListener('click', (event) => {
    event.preventDefault();
    sourceOpener.click();
  });

  return button;
};

const closeInfoPopovers = (root, except) => {
  root.querySelectorAll('.codex-size-split__info-trigger[aria-expanded="true"]').forEach((button) => {
    if (button === except) return;
    button.setAttribute('aria-expanded', 'false');
  });
};

const createControlGroup = ({ title, helper, ariaLabel, values, type, state, group }) => {
  const section = document.createElement('div');
  section.className = 'codex-size-split__group';

  const header = document.createElement('div');
  header.className = 'codex-size-split__header';

  const label = document.createElement('div');
  label.className = 'codex-size-split__label';
  label.textContent = title;

  const infoButton = document.createElement('button');
  infoButton.type = 'button';
  infoButton.className = 'codex-size-split__info-trigger';
  infoButton.dataset.tooltip = helper;
  infoButton.dataset.tooltipPlacement = 'top';
  infoButton.setAttribute('aria-label', `${title}. ${helper}`);
  infoButton.setAttribute('aria-expanded', 'false');
  infoButton.append(createInfoIcon());

  infoButton.addEventListener('click', () => {
    const root = section.closest(SIZE_SPLIT_SELECTOR);
    const shouldOpen = infoButton.getAttribute('aria-expanded') !== 'true';

    if (root) closeInfoPopovers(root, infoButton);

    infoButton.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
  });

  header.append(label, infoButton);

  const options = document.createElement('div');
  options.className = 'codex-size-split__options';
  options.setAttribute('role', 'group');
  options.setAttribute('aria-label', ariaLabel);

  values.forEach((value) => {
    const disabled = type === 'height' && !findCombination(group, state.width, value)?.available;
    const button = createButton({
      label: formatDimension(value),
      value,
      type,
      active: state[type] === value,
      disabled,
    });
    options.append(button);
  });

  section.append(header, options);
  return section;
};

const mountSplitSelector = (picker, group) => {
  const selected = findSelectedCombination(group);
  const state = {
    width: selected.width,
    height: selected.height,
  };

  const root = document.createElement('div');
  root.className = 'codex-size-split';
  root.dataset.codexSizeSplit = 'true';

  const widthGroup = createControlGroup({
    title: 'Largeur du panneau',
    helper: 'Mesurez la largeur à couvrir. Pour un bel effet ondulé, prévoyez plus large que la fenêtre.',
    ariaLabel: 'Choisir la largeur du panneau',
    values: group.widths,
    type: 'width',
    state,
    group,
  });
  const popupProxy = createPopupProxy(group);
  if (popupProxy) widthGroup.querySelector('.codex-size-split__header')?.append(popupProxy);

  const heightGroup = createControlGroup({
    title: 'Hauteur du panneau',
    helper: "Mesurez du haut de la tringle jusqu'au point d'arrêt souhaité.",
    ariaLabel: 'Choisir la hauteur du panneau',
    values: group.heights,
    type: 'height',
    state,
    group,
  });

  root.append(widthGroup, heightGroup);

  root.addEventListener('click', (event) => {
    const button = event.target.closest('button[data-codex-width], button[data-codex-height]');
    if (!button || button.disabled) return;

    closeInfoPopovers(root);

    if (button.dataset.codexWidth) {
      state.width = button.dataset.codexWidth;
      state.height = findBestHeight(group, state.width, state.height);
    } else {
      state.height = button.dataset.codexHeight;
    }

    updateButtons(root, group, state);
    selectOriginalControl(group, findCombination(group, state.width, state.height));
  });

  group.container.before(root);
 group.container.classList.add(SOURCE_CLASS);
 group.container.classList.toggle(
  `${SOURCE_CLASS}--has-popup`,
  Boolean(group.container.querySelector('dialog-component'))
 );
};

const enhancePicker = (picker) => {
  if (picker.querySelector(SIZE_SPLIT_SELECTOR) && picker.querySelector(`.${SOURCE_CLASS}`)) return;

  picker.querySelectorAll(SIZE_SPLIT_SELECTOR).forEach((node) => node.remove());
 picker.querySelectorAll(`.${SOURCE_CLASS}`).forEach((node) =>
  node.classList.remove(SOURCE_CLASS, `${SOURCE_CLASS}--has-popup`)
 );

  const group = findSizeGroup(picker);
  if (!group) return;

  mountSplitSelector(picker, group);
};

const scheduleEnhance = (picker) => {
  if (picker.codexSizeSplitQueued) return;
  picker.codexSizeSplitQueued = true;
  requestAnimationFrame(() => {
    picker.codexSizeSplitQueued = false;
    enhancePicker(picker);
  });
};

const observePicker = (picker) => {
  if (picker.codexSizeSplitObserved) return;
  picker.codexSizeSplitObserved = true;
  scheduleEnhance(picker);

  new MutationObserver(() => scheduleEnhance(picker)).observe(picker, {
    childList: true,
    subtree: true,
  });
};

const observePickersWithin = (root) => {
  if (root instanceof Element && root.matches('variant-picker')) observePicker(root);
  root.querySelectorAll?.('variant-picker').forEach(observePicker);
};

const init = () => {
  observePickersWithin(document);

  const discoveryObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) observePickersWithin(node);
      });
    });
  });

  discoveryObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

document.addEventListener('click', (event) => {
  const target = event.target;

  document.querySelectorAll(`${SIZE_SPLIT_SELECTOR} .codex-size-split__info-trigger[aria-expanded="true"]`).forEach((button) => {
    if (target instanceof Node && button.contains(target)) return;
    button.setAttribute('aria-expanded', 'false');
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  document.querySelectorAll(`${SIZE_SPLIT_SELECTOR} .codex-size-split__info-trigger[aria-expanded="true"]`).forEach((button) => {
    button.setAttribute('aria-expanded', 'false');
  });
});

const style = document.createElement('style');
style.textContent = `
 .codex-size-split {
  display: flex;
  flex-direction: column;
  gap: 14px;
 }

  .codex-size-split__group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

 .codex-size-split__header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
 }

 .codex-size-split__label {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--color-foreground, currentColor);
  line-height: 1.2;
 }

  .codex-size-split__info-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 24px;
  height: 24px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: color-mix(in srgb, var(--color-foreground, currentColor) 72%, var(--color-background, transparent));
  cursor: pointer;
  line-height: 1;
  padding: 0;
    transition: color 160ms ease, transform 160ms ease;
  }

 .codex-size-split__info-trigger:hover,
 .codex-size-split__info-trigger[aria-expanded="true"] {
  color: var(--color-foreground, currentColor);
  transform: translateY(-1px);
 }

 .codex-size-split__info-trigger:focus-visible {
  outline: 1px solid var(--color-selected-input-border, var(--color-input-border, var(--color-border, currentColor)));
  outline-offset: 2px;
 }

 .codex-size-split__info-icon {
  --icon-size: 20;
  pointer-events: none;
 }

 .codex-size-split__popup-link {
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--color-foreground, currentColor);
  cursor: pointer;
  font: inherit;
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.2;
  margin-inline-start: auto;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 0.18em;
 }

 .codex-size-split__popup-link:hover {
  color: color-mix(in srgb, var(--color-foreground, currentColor) 78%, var(--color-background, transparent));
 }

 .codex-size-split__popup-link:focus-visible {
  outline: 1px solid var(--color-selected-input-border, var(--color-input-border, var(--color-border, currentColor)));
  outline-offset: 3px;
 }

  .codex-size-split__info-trigger::after {
    content: attr(data-tooltip);
    position: absolute;
    z-index: 30;
    top: calc(100% + 10px);
    left: 50%;
  width: 280px;
  max-width: calc(100vw - 32px);
  padding: 10px 12px;
  border: 1px solid var(--color-border, var(--color-input-border, currentColor));
  border-radius: 4px;
  background: var(--color-background, Canvas);
  box-shadow: 0 10px 24px color-mix(in srgb, var(--color-foreground, currentColor) 14%, transparent);
  color: var(--color-foreground, currentColor);
  font-size: 0.84rem;
  line-height: 1.4;
  text-align: left;
    white-space: normal;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
    transform: translateX(-50%) translateY(-4px);
    transition: opacity 140ms ease, transform 140ms ease, visibility 140ms ease;
  }

  .codex-size-split__info-trigger::before {
    content: '';
    position: absolute;
    z-index: 31;
    top: calc(100% + 5px);
    left: 50%;
  width: 9px;
  height: 9px;
  border-left: 1px solid var(--color-border, var(--color-input-border, currentColor));
  border-top: 1px solid var(--color-border, var(--color-input-border, currentColor));
  background: var(--color-background, Canvas);
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
    transform: translateX(-50%) rotate(45deg);
    transition: opacity 140ms ease, visibility 140ms ease;
  }

  .codex-size-split__info-trigger:hover::after,
  .codex-size-split__info-trigger:focus-visible::after,
  .codex-size-split__info-trigger[aria-expanded="true"]::after {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }

 .codex-size-split__info-trigger:hover::before,
 .codex-size-split__info-trigger:focus-visible::before,
 .codex-size-split__info-trigger[aria-expanded="true"]::before {
  opacity: 1;
  visibility: visible;
 }

 .codex-size-split__info-trigger[data-tooltip-placement="top"]::after {
  top: auto;
  bottom: calc(100% + 10px);
  transform: translateX(-50%) translateY(4px);
 }

 .codex-size-split__info-trigger[data-tooltip-placement="top"]::before {
  top: auto;
  bottom: calc(100% + 5px);
  transform: translateX(-50%) rotate(225deg);
 }

 .codex-size-split__info-trigger[data-tooltip-placement="top"]:hover::after,
 .codex-size-split__info-trigger[data-tooltip-placement="top"]:focus-visible::after,
 .codex-size-split__info-trigger[data-tooltip-placement="top"][aria-expanded="true"]::after {
  transform: translateX(-50%) translateY(0);
 }

 @media screen and (min-width: 990px) {
  .codex-size-split__info-trigger::after,
  .codex-size-split__info-trigger[data-tooltip-placement="top"]::after {
   top: 50%;
   bottom: auto;
   left: calc(100% + 12px);
   transform: translateY(-50%) translateX(-4px);
  }

  .codex-size-split__info-trigger::before,
  .codex-size-split__info-trigger[data-tooltip-placement="top"]::before {
   top: 50%;
   bottom: auto;
   left: calc(100% + 7px);
   transform: translateY(-50%) rotate(-45deg);
  }

  .codex-size-split__info-trigger:hover::after,
  .codex-size-split__info-trigger:focus-visible::after,
  .codex-size-split__info-trigger[aria-expanded="true"]::after,
  .codex-size-split__info-trigger[data-tooltip-placement="top"]:hover::after,
  .codex-size-split__info-trigger[data-tooltip-placement="top"]:focus-visible::after,
  .codex-size-split__info-trigger[data-tooltip-placement="top"][aria-expanded="true"]::after {
   transform: translateY(-50%) translateX(0);
  }
 }

 .codex-size-split__options {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

 .codex-size-split__option {
  min-height: max(38px, calc(var(--variant-button-min-height, 40px) - 2px));
  min-width: max(68px, calc(var(--variant-button-min-width, 80px) - 12px));
  border: var(--variant-button-border-width, 1px) solid var(--color-variant-border, currentColor);
  border-radius: var(--inputs-border-radius, 4px);
  padding: max(6px, calc(var(--variant-button-padding-vertical, 10px) - 3px)) max(12px, calc(var(--variant-button-padding-horizontal, 20px) - 8px));
  background: var(--color-variant-background, transparent);
  color: var(--color-variant-text, inherit);
    cursor: pointer;
    font: inherit;
    font-weight: var(--button-primary-font-weight, 600);
    transition: border-color 160ms ease, background-color 160ms ease, color 160ms ease;
  }

 .codex-size-split__option:hover:not(:disabled),
 .codex-size-split__option.is-active {
  border-color: var(--color-selected-variant-border, var(--color-selected-input-border, var(--color-foreground, currentColor)));
  background: var(--color-selected-variant-background, var(--color-selected-input-background, var(--color-foreground, currentColor)));
  color: var(--color-selected-variant-text, var(--color-selected-input-text, var(--color-background, Canvas)));
 }

  .codex-size-split__option:disabled {
    cursor: not-allowed;
    opacity: 0.34;
  }

 .codex-size-split-source:not(.codex-size-split-source--has-popup) {
  display: none !important;
 }

 .codex-size-split-source--has-popup {
  position: absolute !important;
  inline-size: 1px !important;
  block-size: 1px !important;
  border: 0 !important;
  clip: rect(0 0 0 0) !important;
  clip-path: inset(50%) !important;
  margin: -1px !important;
  overflow: hidden !important;
  padding: 0 !important;
  white-space: nowrap !important;
 }

 .codex-size-split-source--has-popup [data-ref="popup-modal"] {
  white-space: normal;
 }
`;
document.head.append(style);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
